// Implements a subset of the server API but backs onto xDomainLocalStorage in the browser
import * as async from "async";
import Backbone from "backbone";
import ObjectId from "ObjectId";
import * as _ from "underscore";

import * as readlangLocalStorage from "readlangLocalStorage";
import * as dateUtils from "shared/dateUtils";
import * as languageMap from "shared/languageMap";
import perDayAverage from "shared/perDayAverage";
import * as spacedRepetition from "shared/spacedRepetition";
import * as utils from "shared/utils";

export const limits = {
  books: 5,
  bookLength: 1024 * 50, // characters
  words: 500,
};
const DummyUserWord = Backbone.Model.extend({
  idAttribute: "_id",
  save: function (changeHash) {
    this.set(changeHash);
  },
});

export const clearLocalStorage = (callback) => {
  readlangLocalStorage.clearWithExceptions(
    ["^mongoMachineId$", "^reader\\..+", "referrer", "couponToken"],
    callback
  );
};

const getItemJson = (key, callback) => {
  readlangLocalStorage.getItem(key, (value) => {
    if (value) {
      callback(JSON.parse(value));
    } else {
      callback(null);
    }
  });
};
const setItemJson = (key, value, callback) => {
  readlangLocalStorage.setItem(key, JSON.stringify(value), callback);
};

export const isLocalMode = async () =>
  (await readlangLocalStorage.getItemPromise("localMode")) === "true" ||
  // @ts-ignore
  (await readlangLocalStorage.getItemPromise("localMode")) === true;

export const setLocalMode = async (localMode) => {
  await readlangLocalStorage.setItemPromise("localMode", localMode);
};

/** @type {(callback: (userWords: Partial<UserWord>[]) => void) => void} */
const getUserWords = (callback) => {
  getItemJson("userWords", (result) => {
    callback(result || []);
  });
};
const setUserWords = (newUserWords, callback) => {
  // error checking for duplicates
  const wordLangMap = {};

  _.each(newUserWords, (newUserWord) => {
    const wordLang = newUserWord.word + "_" + newUserWord.language;
    wordLangMap[wordLang] = 1;
  });

  setItemJson("userWords", newUserWords, callback);
};

const getUser = (callback) => {
  getItemJson("user", (result) => {
    callback(
      result || {
        username: "Guest",
        currentLearningLanguage: "es",
        firstLanguage: "en",
        books: {},
        trialMode: true,
        _id: "local",
      }
    );
  });
};
const setUser = (newUser, callback) => {
  setItemJson("user", newUser, callback);
};

export const pushSync = (remoteRequest, callback) => {
  getUser((user) => {
    getUserWords(async (userWords) => {
      if (!(await isLocalMode())) {
        callback();
        return;
      }

      // go online
      setLocalMode(false);

      const numBooks = Object.keys(user.books).length || 0;

      const onlineUser: any = _.pick(
        user,
        "firstLanguage",
        "currentLearningLanguage",
        "acheivements",
        "readerFont",
        "readerAlign",
        "readerSentenceBreaks",
        "readerTranslationStyle",
        "readerMergePhrases",
        "readerWordHighlights",
        "bookmarkletPosition"
      );

      onlineUser.addBooks = {};

      if (await readlangLocalStorage.getItemPromise("referrer")) {
        onlineUser.referrer =
          await readlangLocalStorage.getItemPromise("referrer");
      }

      if (await readlangLocalStorage.getItemPromise("httpReferrer")) {
        onlineUser.referrer =
          await readlangLocalStorage.getItemPromise("httpReferrer");
      }

      // sync user
      remoteRequest("PUT", "/api/user", onlineUser, (data) => {
        if (data.error) {
          console.error("push sync fail: %s", JSON.stringify(data));
          return;
        }
        readlangLocalStorage.removeItemPromise("referrer");
        readlangLocalStorage.removeItemPromise("httpReferrer");

        if (numBooks === 0 && userWords.length === 0) {
          clearLocalStorage(callback);
          return;
        }

        // add public books
        _.each(user.books, (userBookInfo, bookID) => {
          onlineUser.addBooks[bookID] = user.books[bookID];
        });

        // sync all books
        async.each(
          userWords,
          (userWord, asyncCallback) => {
            remoteRequest("POST", "/api/userWord", userWord, () => {
              asyncCallback();
            });
          },
          () => {
            clearLocalStorage(() => {
              callback({ pushSyncSuccess: true });
            });
          }
        );
      });
    });
  });
};

export const request = async (
  method,
  path,
  data,
  callback,
  errorCallback,
  remoteRequest
) => {
  const splitURL = path.split("?");
  let splitPath = splitURL[0].split("/");

  data = data || {};

  const queryString = splitURL[1];
  if (queryString) {
    throw "Shouldn't use query string, use data instead";
  }

  if (splitPath[0] === "") {
    splitPath.splice(0, 1);
  }

  if (splitPath[0] !== "api") {
    return false;
  }

  let bookID;

  // -- Always try local API first for these --
  if (method === "GET" && splitPath[1] === "chunk" && splitPath.length > 2) {
    remoteRequest(method, path, data, callback, errorCallback);

    return true;
  }

  if (!(await isLocalMode())) {
    return false;
  }

  // -- Only use these if we are in local mode
  if (method === "GET" && splitPath[1] === "book" && splitPath.length > 2) {
    remoteRequest(method, path, data, callback, errorCallback);
  } else if (method === "POST" && splitPath[1] === "login") {
    remoteRequest(
      method,
      path,
      data,
      (responseData) => {
        pushSync(remoteRequest, (pushSyncData) => {
          callback(_.extend(responseData, pushSyncData || {}));
        });
      },
      errorCallback
    );
  } else if (method === "GET" && splitPath[1] === "translate") {
    remoteRequest(method, path, data, callback, errorCallback);
  } else if (method === "POST" && splitPath[1] === "user") {
    remoteRequest(method, path, data, callback, errorCallback);
  } else if (method === "GET" && splitPath[1] === "languages") {
    remoteRequest(method, path, data, callback, errorCallback);
  } else if (method === "GET" && splitPath[1] === "urls") {
    remoteRequest(method, path, data, callback, errorCallback);
  } else if (method === "GET" && splitPath[1] === "url") {
    remoteRequest(method, path, data, callback, errorCallback);
  } else if (method === "POST" && splitPath[1] === "splitTests") {
    remoteRequest(method, path, data, callback, errorCallback);
  } else if (method === "GET" && splitPath[1] === "shelf") {
    remoteRequest(method, path, data, callback, errorCallback);
  } else if (method === "GET" && splitPath[1] === "shelves") {
    remoteRequest(method, path, data, callback, errorCallback);
  } else if (method === "POST" && splitPath[1] === "experiment") {
    remoteRequest(method, path, data, callback, errorCallback);
  } else if (method === "GET" && splitPath[1] === "webPushNotifications") {
    // Return empty list
    callback([]);
  } else if (method === "DELETE" && splitPath[1] === "webPushNotifications") {
    // no-op
    callback({});
  } else if (method === "GET" && splitPath[1] === "readyToStartFlashcards") {
    getUserWords((_userWords) => {
      getUser((user) => {
        const userWords = _.filter(
          _userWords,
          (userWord) =>
            userWord.language ===
            (data.language || user.currentLearningLanguage)
        );

        let countStarted = 0;
        let countReady = 0;
        _.each(userWords, (userWord) => {
          if ("interval" in userWord) {
            countStarted++;
          } else {
            countReady++;
          }
        });

        if (countStarted > 0) {
          callback({ error: "alreadyStarted" });
        } else {
          callback({ count: countReady });
        }
      });
    });
  } else if (method === "GET" && splitPath[1] === "listAllWords") {
    getUserWords((_userWords) => {
      getUser((user) => {
        let userWords = _.filter(
          _userWords,
          (userWord) =>
            userWord.language === (splitPath[2] || user.currentLearningLanguage)
        );

        if (data.filter === "readyToLearn") {
          userWords = _.filter(
            userWords,
            (userWord) =>
              !userWord.nextDate ||
              dateUtils.parse(userWord.nextDate) < new Date().getTime()
          );
        }

        callback(_.pluck(userWords, "word"));
      });
    });
  } else if (
    method === "GET" &&
    splitPath[1] === "v2" &&
    splitPath[2] === "userWords"
  ) {
    if (!data.language || !data.exactPhrasesSearch) {
      handleError(method, path, errorCallback);
    }

    const phraseRegExp = new RegExp(
      _.map(
        data.exactPhrasesSearch.split("|"),
        () => "^" + utils.regexpEscape(data.exactPhrasesSearch) + "$"
      ).join("|"),
      "i"
    );

    getUserWords((_userWords) => {
      const userWords = _.filter(
        _userWords,
        (userWord) =>
          userWord.language === data.language &&
          phraseRegExp.test(userWord.word)
      );
      callback({
        count: userWords.length,
        userWords: userWords,
      });
    });
  } else if (method === "GET" && splitPath[1] === "userWords") {
    getUser((user) => {
      getUserWords((unfilteredUserWords) => {
        let userWords = _.filter(
          unfilteredUserWords,
          (userWord) =>
            userWord.language ===
            (data.language || user.currentLearningLanguage)
        );

        let filterFunction;

        switch (data.wordGroup) {
          case "notStarted":
            filterFunction = (word) => !word.interval;
            break;
          case "justStarted":
            filterFunction = (word) =>
              word.interval < spacedRepetition.GETTING_THERE_INTERVAL;
            break;
          case "gettingThere":
            filterFunction = (word) =>
              word.interval >= spacedRepetition.GETTING_THERE_INTERVAL &&
              word.interval < spacedRepetition.MASTERED_INTERVAL;
            break;
          case "mastered":
            filterFunction = (word) =>
              word.interval >= spacedRepetition.MASTERED_INTERVAL;
            break;
          case "reviewedSince":
            filterFunction = (word) =>
              word.previousIntervalDate &&
              word.previousIntervalDate > data.timestamp * 1000;
            break;
          case "readyToTest":
            filterFunction = (word) =>
              !word.interval ||
              (word.interval && new Date(word.nextDate) > new Date());
            break;
          default:
            break;
        }

        if (filterFunction) {
          userWords = _.filter(userWords, filterFunction);
        }
        callback(userWords);
      });
    });
  } else if (method === "GET" && splitPath[1] === "userWordCounts") {
    getUser((user) => {
      getUserWords((unfilteredUserWords) => {
        const userWords = _.filter(
          unfilteredUserWords,
          (userWord) =>
            userWord.language ===
            (data.language || user.currentLearningLanguage)
        );
        const counts = {
          notStarted: 0,
          justStarted: 0,
          gettingThere: 0,
          mastered: 0,
          scheduled: 0,
          readyToTest: 0,
          averagePerDay: 0,
        };

        _.each(userWords, (word) => {
          if (!word.interval) {
            counts.notStarted++;
          } else {
            counts.averagePerDay += perDayAverage(word);
            if (word.interval < spacedRepetition.GETTING_THERE_INTERVAL) {
              counts.justStarted++;
            }
            if (
              word.interval >= spacedRepetition.GETTING_THERE_INTERVAL &&
              word.interval < spacedRepetition.MASTERED_INTERVAL
            ) {
              counts.gettingThere++;
            }
            if (word.interval >= spacedRepetition.MASTERED_INTERVAL) {
              counts.mastered++;
            }
            if (new Date(word.nextDate) < new Date()) {
              counts.scheduled++;
            }
          }
        });
        callback(counts);
      });
    });
  } else if (method === "GET" && splitPath[1] === "user") {
    if (splitPath.length > 2) {
      return false; // handle remotely
    } else {
      getUser(callback);
    }
  } else if (method === "GET" && splitPath[1] === "books") {
    remoteRequest(method, path, data, callback, errorCallback);
  } else if (method === "GET" && splitPath[1] === "popularDomains") {
    remoteRequest(method, path, data, callback, errorCallback);
  } else if (
    method === "DELETE" &&
    splitPath[1] === "book" &&
    splitPath.length > 2
  ) {
    throw Error("Book deletion not supported in local mode");
  } else if (
    method === "DELETE" &&
    splitPath[1] === "userWord" &&
    splitPath.length > 2
  ) {
    getUserWords((userWords) => {
      let index = -1;
      let id = splitPath[2];
      _.each(userWords, (word, i) => {
        if (word._id === id) {
          index = i;
          return false;
        }
      });

      if (index !== -1) {
        userWords.splice(index, 1);
        setUserWords(userWords, callback);
      } else {
        callback();
      }
    });
  } else if (
    method === "GET" &&
    splitPath[1] === "userWord" &&
    splitPath.length > 2
  ) {
    getUserWords((userWords) => {
      /** @type {any} */
      let theWord = -1;
      let id = splitPath[2];

      _.each(userWords, (word) => {
        if (word._id === id) {
          theWord = word;
          return false;
        }
      });

      if (theWord) {
        callback(theWord);
      } else {
        callback({ error: "not found" });
      }
    });
  } else if (
    method === "POST" &&
    splitPath[1] === "userWord" &&
    splitPath[3] === "recall"
  ) {
    getUserWords((userWords) => {
      const userWordID = splitPath[2];
      const word = _.find(userWords, (thisWord) => userWordID === thisWord._id);
      if (word) {
        const wordModel = new DummyUserWord(word);
        spacedRepetition.wordRecalled(
          wordModel,
          data.recallEase,
          new Date(),
          data.strength,
          data.repeatTime
        );
        _.extend(word, wordModel.toJSON());

        setUserWords(userWords, () => {
          callback({ success: true });
        });
      } else {
        callback({ error: "Word not found" });
      }
    });
  } else if (
    method === "POST" &&
    /\/api\/userWord/.test(path) &&
    splitPath.length === 2
  ) {
    getUserWords((userWords) => {
      let word: Partial<UserWord>;
      // check for existing
      if (languageMap.map[data.language].capitalizationImportant) {
        word = _.find(
          userWords,
          (thisWord) =>
            data.word === thisWord.word && data.language === thisWord.language
        );
      } else {
        const regExp = new RegExp(
          "^" + utils.regexpEscape(data.word) + "$",
          "i"
        );
        word = _.find(
          userWords,
          (thisWord) =>
            regExp.test(thisWord.word) && data.language === thisWord.language
        );
      }

      const result: any = { success: true };

      // add new context if exists
      if (word) {
        _.extend(word, _.omit(data, ["word", "translation"]));

        if (word.word !== data.word && /[a-z]/.test(data.word[0])) {
          // prefer version with lower case first letter
          word.word = data.word;
        }

        // interpret a lookup as meaning user wasn't sure of word
        if (word && word.interval > 0) {
          const tempUserWord = new DummyUserWord(word);
          spacedRepetition.wordRecalled(tempUserWord, 2, new Date(), 0.5);
          _.extend(word, tempUserWord.toJSON());
        }

        setUserWords(userWords, () => {
          result.updated = word._id;
          callback(result);
        });
      } else {
        if (userWords.length > limits.words) {
          callback({
            error:
              "The free trial version is limited to " +
              limits.words +
              " words, please signup for more.",
          });
          return;
        }

        word = {
          word: data.word,
          translation: data.translation,
          _id: new ObjectId().toString(),
          // no userID in localAPI
        };

        _.extend(word, _.omit(data, ["word", "translation"]));

        remoteRequest(
          "GET",
          "/api/wordFrequency",
          {
            language: word.language,
            word: word.word.toLowerCase(),
          },
          (response) => {
            if (response.frequency) {
              word.frequency = response.frequency;
              result.frequency = response.frequency;
            }
            userWords.push(word);
            setUserWords(userWords, () => {
              result.upserted = word._id;
              callback(result);
            });
          },
          () => {
            userWords.push(word);
            setUserWords(userWords, () => {
              result.upserted = word._id;
              callback(result);
            });
          }
        );
      }
    });
  } else if (
    method === "PATCH" &&
    splitPath[1] === "book" &&
    splitPath[3] === "userBook"
  ) {
    bookID = splitPath[2];
    getUser((user) => {
      user.books[bookID] = _.extend(user.books[bookID] || {}, data);
      setUser(user, () => {
        callback({
          success: true,
        });
      });
    });
  } else if (method === "POST" && /\/api\/library/.test(path)) {
    throw Error("Not supported in local mode");
  } else if (
    (method === "PUT" || method === "PATCH") &&
    splitPath[1] === "userWord"
  ) {
    splitPath = path.split("?")[0].split("/");
    getUserWords((userWords) => {
      const userWordID = splitPath[splitPath.length - 1];
      const word = _.find(userWords, (thisWord) => userWordID === thisWord._id);
      if (word) {
        _.extend(word, data);
        setUserWords(userWords, () => {
          callback({ success: true });
        });
      } else {
        callback({ error: "Word not found" });
      }
    });
  } else if (
    (method === "PUT" || method === "PATCH") &&
    splitPath[1] === "user"
  ) {
    // no password stuff needed for local version
    getUser((user) => {
      _.extend(
        user,
        _.pick(
          data,
          "firstLanguage",
          "currentLearningLanguage",
          "sortBooksBy",
          "publicBooksCategories",
          "acheivements",
          "username",
          "location",
          "homepageURL",
          "about",
          "customDictionaries",
          "typingMode",
          "passiveMode",
          "passiveModeLanguages",
          "publicBooksFilter",
          "readerFont",
          "readerAlign",
          "readerSentenceBreaks",
          "readerTranslationStyle",
          "readerMergePhrases",
          "readerWordHighlights",
          "readerTranslations",
          "bookmarkletPosition"
        )
      );

      setUser(user, callback);
    });
  } else if (method === "GET" && /\/api\/logout/.test(path)) {
    if (
      confirm(
        "Warning - since you haven't registered, logging out will delete *all* your words!\n\n" +
          "Are you sure you want delete everything?"
      )
    ) {
      clearLocalStorage(callback);
    } else {
      callback();
    }
  } else if (method === "GET" && /\/api\/currentDate/.test(path)) {
    callback({ currentDate: new Date() });
  } else if (method === "GET" && /\/api\/groups/.test(path)) {
    callback({ adminGroups: [], memberGroups: [] });
  } else if (method === "GET" && splitPath[1] === "dashboard") {
    callback([]);
  } else {
    handleError(method, path, errorCallback);
  }

  // indicate that we've handled this request
  return true;
};

var handleError = (method, path, errorCallback) => {
  const errorMessage = "local request not supported " + method + " " + path;
  console.error(errorMessage);
  if (errorCallback) {
    errorCallback(errorMessage);
  } else {
    throw errorMessage;
  }
};
